import { useEffect } from 'react';
import { connect } from 'react-redux';
import api from 'api';
import { UPDATE_LEARNER, UPDATE_TEAM } from 'actions/types';
import { isMobileAppRuntime } from 'utils';

const SNAPSHOT_KEY = 'flc.mobile.callHome.snapshot.v1';
const hydratedMemberIds = new Set();

const getCapacitorNetworkPlugin = () => {
  if (typeof window === 'undefined') return null;
  return window.Capacitor?.Plugins?.Network || null;
};

const isWifiConnection = async () => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  const networkPlugin = getCapacitorNetworkPlugin();
  if (networkPlugin?.getStatus) {
    try {
      const status = await networkPlugin.getStatus();
      return status?.connected === true && String(status?.connectionType || '').toLowerCase() === 'wifi';
    } catch (_) {
      return false;
    }
  }

  const browserConnection = typeof navigator !== 'undefined'
    ? (navigator.connection || navigator.mozConnection || navigator.webkitConnection)
    : null;
  const connectionType = String(browserConnection?.type || '').toLowerCase();
  return connectionType === 'wifi';
};


const readSnapshots = () => {
  try {
    const value = JSON.parse(window.localStorage.getItem(SNAPSHOT_KEY) || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch (_) {
    return {};
  }
};

const writeSnapshot = (member) => {
  if (!member?._id) return;
  const snapshots = readSnapshots();
  snapshots[String(member._id)] = {
    familyMemberId: String(member._id),
    savedAt: new Date().toISOString(),
    snapshot: {
      reports: member.reports || {},
      timeInApp: Array.isArray(member.timeInApp) ? member.timeInApp : [],
      learningGoals: Array.isArray(member.learningGoals) ? member.learningGoals : [],
    },
  };
  try { window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshots)); } catch (_) {}
};

const getSnapshot = (familyMemberId) => readSnapshots()[String(familyMemberId || '')] || null;

const MobileCallHomeSync = ({ learner, team, familyMembers, dispatch }) => {
  useEffect(() => {
    if (!isMobileAppRuntime()) return undefined;

    const activeTeam = Array.isArray(team) ? team : [];
    const activeMembers = [learner, ...activeTeam].filter(Boolean);
    const activeOriginal = activeMembers.find(member => member?.customContentOnboarding?.originalMember === true);

    if (activeOriginal?._id) {
      const id = String(activeOriginal._id);
      const local = getSnapshot(id);
      if (!hydratedMemberIds.has(id)) {
        hydratedMemberIds.add(id);
        if (local?.snapshot) {
          const hydrated = { ...activeOriginal, ...local.snapshot };
          if (String(learner?._id || '') === id) {
            dispatch({ type: UPDATE_LEARNER, filter: 'flinkPlay', payload: hydrated });
          } else {
            dispatch({
              type: UPDATE_TEAM,
              filter: 'flinkPlay',
              payload: activeTeam.map(member => String(member?._id || '') === id ? hydrated : member),
            });
          }
        } else {
          writeSnapshot(activeOriginal);
        }
      } else {
        writeSnapshot(activeOriginal);
      }
    }

    const knownMembers = [...activeMembers, ...(Array.isArray(familyMembers) ? familyMembers : [])].filter(Boolean);
    const originalMember = knownMembers.find(member => member?.customContentOnboarding?.originalMember === true);
    if (!originalMember?._id) return undefined;

    let active = true;
    let syncing = false;

    const syncNow = async () => {
      if (!active || syncing || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
      if (!(await isWifiConnection())) return;
      syncing = true;
      try {
        const status = await api.mobileRuntime.getCallHomeStatus();
        if (!active || !status?.enabled) return;
        if (String(status?.originalFamilyMemberId || '') !== String(originalMember._id)) return;
        const nextAt = status?.nextSyncAt ? new Date(status.nextSyncAt).getTime() : 0;
        if (nextAt && nextAt > Date.now()) return;

        const local = getSnapshot(originalMember._id);
        if (!local?.snapshot) return;
        await api.mobileRuntime.callHome(originalMember._id, local.snapshot);
      } catch (error) {
        console.warn('[mobile-call-home] sync skipped', error?.message || error);
      } finally {
        syncing = false;
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') syncNow();
    };

    const networkPlugin = getCapacitorNetworkPlugin();
    let networkListener = null;
    if (networkPlugin?.addListener) {
      Promise.resolve(networkPlugin.addListener('networkStatusChange', (status) => {
        if (status?.connected && String(status?.connectionType || '').toLowerCase() === 'wifi') syncNow();
      })).then((listener) => { networkListener = listener; }).catch(() => {});
    }

    syncNow();
    window.addEventListener('online', syncNow);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      if (networkListener?.remove) networkListener.remove();
      window.removeEventListener('online', syncNow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [learner, team, familyMembers, dispatch]);

  return null;
};

const mapStateToProps = ({ flinkPlay }) => ({
  learner: flinkPlay.learner,
  team: flinkPlay.team,
  familyMembers: flinkPlay.familyMembers,
});

export default connect(mapStateToProps)(MobileCallHomeSync);
